import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth-context';
import { API_URL } from '@/lib/config';

interface User {
  id: string;
  _id: string;  // Adding _id field since the backend seems to use it
  email: string;
  role: string;
  status_id: string | null;
}

interface ResetPasswordResult {
  newPassword: string;
  message: string;
}

const AdminDashboard = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [resetPasswordResult, setResetPasswordResult] = useState<ResetPasswordResult | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const { user } = useAuth();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (!user || !user.token) {
          throw new Error('User not authenticated');
        }

        const response = await fetch(`${API_URL}/users`, {
          headers: {
            'Authorization': `Bearer ${user.token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }

        const data = await response.json();
        setUsers(data);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    if (user?.token) {
        fetchUsers();
    }

  }, [user]);

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      if (!user || !user.token) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${API_URL}/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        throw new Error('Failed to update user role');
      }

      // Update local state
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === userId ? { ...u, role: newRole } : u
        )
      );
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  const deleteUser = async (userId: string) => {
    try {
      if (!user || !user.token) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${API_URL}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      // Update local state
      setUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const resetPassword = async (userId: string) => {
    try {
      if (!user || !user.token) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${API_URL}/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to reset password');
      }

      const data = await response.json();
      setResetPasswordResult(data);
      // Find user to show in modal
      const targetUser = users.find(u => u._id === userId || u.id === userId);
      if (targetUser) {
        setSelectedUser(targetUser);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
    }
  };

  const closeModal = () => {
    setResetPasswordResult(null);
    setSelectedUser(null);
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-6">User Management</h1>
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id || user._id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={user.role}
                      onChange={(e) => updateUserRole(user.id || user._id, e.target.value)}
                      className="text-sm text-gray-900 border rounded px-2 py-1"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm flex space-x-4">
                    <button
                      onClick={() => resetPassword(user.id || user._id)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Reset Password
                    </button>
                    <button
                      onClick={() => deleteUser(user.id || user._id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Password Reset Modal */}
      {resetPasswordResult && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Password Reset</h2>
            <p className="mb-2">User: <span className="font-medium">{selectedUser.email}</span></p>
            <p className="mb-4">{resetPasswordResult.message}</p>
            
            <div className="bg-gray-100 p-3 rounded mb-4">
              <p className="text-sm text-gray-700 mb-1">New Password:</p>
              <p className="font-mono bg-white p-2 border rounded text-sm select-all">{resetPasswordResult.newPassword}</p>
            </div>
            
            <div className="text-sm text-gray-500 mb-4">
              <p>Please provide this password to the user securely.</p>
              <p>They should change it after logging in.</p>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard; 